import React from 'react'
import DOMPurify from 'isomorphic-dompurify'

import VisibilitySensor from '@jarvis.oddle/core/components/VisibilitySensor'
import CategoryLoader from './CategoryLoader'
import ProductCard from './ProductCard'
import ProductCardSkeleton from './ProductCardSkeleton'
import { Row } from '@jarvis.oddle/core/components/Grid'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { numberise, pixelise } from '@jarvis.oddle/core/tools/style'
import {
  header,
  horizontalNav,
  margin,
  marginSizes
} from '@jarvis.oddle/core/theme/default'
import { H2, P } from '@jarvis.oddle/core/components/Typo'
import ExternalContent from '@jarvis.oddle/core/components/ExternalContent'

const CategoryAnchor = styled.a<{
  showHorizontalNav: boolean
}>`
  display: block;
  position: relative;
  visibility: hidden;
  top: -${pixelise(header.mobileHeight)};
  ${breakpoint('desktop')`
    top: -${(props) =>
      pixelise(
        props.showHorizontalNav
          ? numberise(horizontalNav.height) + header.desktopHeight
          : header.desktopHeight
      )};
  `}
`

const CategoryHeader = styled(H2)`
  margin: ${margin[marginSizes.medium]} 0;
`

const CategoryTitle = styled(P).attrs((props) => ({
  color: props.theme.colorSectionHeadingsFont
}))``

interface CategoryProps {
  isCategoryLoaded: boolean
  isCategoryDisplayed: boolean
  categoryVisibilityEmitter: {
    on: (event: string, callback: () => void) => void
    off: (event: string, callback: () => void) => void
    once: (event: string, callback: () => void) => void
    emit: (event: string, { isVisible, category }) => boolean
  }
  category: Menu.MenuData
  showHorizontalNav: boolean
  previewMode: boolean
  showImage: boolean
  showDefaultImage: boolean
  productImageAspect: string
  listView: boolean
  showSideNav: boolean
  showCategoryDetails: (categoryId: string) => void
}

const Category = ({
  isCategoryLoaded,
  isCategoryDisplayed,
  categoryVisibilityEmitter,
  category,
  showHorizontalNav,
  previewMode,
  showImage,
  showDefaultImage,
  productImageAspect,
  listView,
  showSideNav,
  showCategoryDetails
}: CategoryProps): JSX.Element => {
  const onVisibilityChange = React.useCallback(
    (isVisible) => {
      categoryVisibilityEmitter.emit('visibilityChange', {
        isVisible,
        category
      })
    },
    [category, categoryVisibilityEmitter]
  )
  return (
    <VisibilitySensor
      key={category.id}
      rootMargin='-49% 0px -50% 0px' // Vertical center of the page
      onChange={onVisibilityChange}
    >
      <CategoryLoader
        categoryId={category.id}
        showCategoryDetails={showCategoryDetails}
        disabled={isCategoryLoaded || !isCategoryDisplayed}
      >
        <CategoryAnchor
          id={category.id}
          showHorizontalNav={showHorizontalNav}
        />
        {isCategoryDisplayed && (
          <React.Fragment>
            <CategoryHeader sectionHeading>{category.name}</CategoryHeader>
            <ExternalContent previewMode={previewMode}>
              <CategoryTitle
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(category.description) }}
              />
            </ExternalContent>
            <Row>
              {isCategoryLoaded
                ? category.items.map((product, idx) => (
                    <ProductCard
                      key={idx}
                      showImage={showImage}
                      showDefaultImage={showDefaultImage}
                      productImageAspect={productImageAspect}
                      listView={listView}
                      showSideNav={showSideNav}
                      category={category}
                      product={product}
                    />
                  ))
                : (category.items || []).map((_, idx) => (
                    <ProductCardSkeleton
                      categoryId={category.id}
                      showCategoryDetails={showCategoryDetails}
                      key={idx}
                      listView={listView}
                      showSideNav={showSideNav}
                    />
                  ))}
            </Row>
          </React.Fragment>
        )}
      </CategoryLoader>
    </VisibilitySensor>
  )
}

export default Category
